import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Room, RoomEvent, Track, VideoPresets } from 'livekit-client';
import { useAuth } from './AuthContext';
import { useCall, createRinger } from './CallContext';
import { toast } from 'sonner';
import { createRingtoneRinger, getStoredRingtoneChoice, getCustomRingtoneUrl } from '../utils/callRingtones';

const GroupCallContext = createContext(null);
export const useGroupCall = () => useContext(GroupCallContext);

const isConvMuted = (convId) => {
  try { return (JSON.parse(localStorage.getItem('muted_conversation_ids') || '[]')).includes(convId); } catch (e) { return false; }
};

const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// LiveKit ConnectionQuality → same 3-level scale as the 1:1 badge
const mapQuality = (q) => (
  q === 'excellent' ? 'good' : q === 'good' ? 'fair' : (q === 'poor' || q === 'lost') ? 'poor' : null
);

export const GroupCallProvider = ({ children }) => {
  const { api, user, onDeviceUpdate, isAuthenticated } = useAuth();
  const { call: oneToOneCall } = useCall();

  // {call_id, conversation_id, conversation_name, media, status: 'incoming'|'connecting'|'active', initiator_name}
  const [groupCall, setGroupCall] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [participantStates, setParticipantStates] = useState({}); // uid -> invited|joined|left|declined|missed
  const [micMuted, setMicMuted] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [cameraFacing, setCameraFacing] = useState('user');
  const [multipleCameras, setMultipleCameras] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState(null);
  const [encrypted, setEncrypted] = useState(false);
  const [volumeBoost, setVolumeBoost] = useState(() => localStorage.getItem('call_volume_boost') !== 'off');
  const volumeBoostRef = useRef(volumeBoost);
  useEffect(() => { volumeBoostRef.current = volumeBoost; }, [volumeBoost]);
  const [dominantSpeaker, setDominantSpeaker] = useState(null);
  const speakerChangeAtRef = useRef(0);
  const [soloCountdown, setSoloCountdown] = useState(null);
  const soloTimerRef = useRef(null);
  const suppressSoloRef = useRef(false);
  const hadRemoteRef = useRef(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [activeConvCalls, setActiveConvCalls] = useState({}); // conversation_id -> {call_id, media}
  const activeConvCallsRef = useRef({});
  useEffect(() => { activeConvCallsRef.current = activeConvCalls; }, [activeConvCalls]);
  const [callChatToast, setCallChatToast] = useState(null); // {id, sender, text}
  const [callChatUnread, setCallChatUnread] = useState(0);
  const chatToastTimerRef = useRef(null);
  const [activeDevices, setActiveDevices] = useState({}); // kind -> deviceId

  const roomRef = useRef(null);
  const groupCallRef = useRef(null);
  const oneToOneRef = useRef(null);
  const ringRef = useRef(null);
  const timerRef = useRef(null);
  const audioHostRef = useRef(null);
  const incomingTimeoutRef = useRef(null);
  const boostRef = useRef(null);
  const ringbackRef = useRef(null);
  const awayRef = useRef(new Set());
  const screenSharingRef = useRef(false);
  useEffect(() => { screenSharingRef.current = screenSharing; }, [screenSharing]);

  useEffect(() => { groupCallRef.current = groupCall; }, [groupCall]);
  useEffect(() => { oneToOneRef.current = oneToOneCall; }, [oneToOneCall]);

  // Hidden host for remote audio elements
  useEffect(() => {
    const div = document.createElement('div');
    div.style.display = 'none';
    div.setAttribute('data-testid', 'group-call-audio-host');
    document.body.appendChild(div);
    audioHostRef.current = div;
    return () => { div.remove(); };
  }, []);

  const stopRing = useCallback(() => {
    ringRef.current?.stop();
    ringRef.current = null;
    if (incomingTimeoutRef.current) { clearTimeout(incomingTimeoutRef.current); incomingTimeoutRef.current = null; }
  }, []);

  // Speaker loudness boost — same 2.5x gain + soft limiter as 1:1 (skipped on iOS)
  const ensureBoost = () => {
    if (IS_IOS) return null;
    if (boostRef.current) return boostRef.current;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      const ctx = new Ctx();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const gain = ctx.createGain();
      gain.gain.value = volumeBoostRef.current ? 2.5 : 1.0;
      const limiter = ctx.createDynamicsCompressor();
      limiter.threshold.value = -6;
      limiter.ratio.value = 12;
      gain.connect(limiter).connect(ctx.destination);
      boostRef.current = { ctx, gain, sources: new Map() };
    } catch (e) { return null; }
    return boostRef.current;
  };
  const boostAttachTrack = (track, el) => {
    const b = ensureBoost();
    if (!b) return;
    try {
      const src = b.ctx.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
      src.connect(b.gain);
      b.sources.set(track.sid, src);
      el.muted = true; // element remains Chrome's required sink; WebAudio carries playback
    } catch (e) {}
  };
  const boostDetachTrack = (track) => {
    const b = boostRef.current;
    const src = b?.sources.get(track.sid);
    if (src) { try { src.disconnect(); } catch (e) {} b.sources.delete(track.sid); }
  };
  const closeBoost = () => {
    const b = boostRef.current;
    if (b) { try { b.ctx.close(); } catch (e) {} boostRef.current = null; }
  };
  const toggleVolumeBoost = useCallback(() => {
    setVolumeBoost(v => {
      const next = !v;
      localStorage.setItem('call_volume_boost', next ? 'on' : 'off');
      if (boostRef.current) boostRef.current.gain.gain.value = next ? 2.5 : 1.0;
      return next;
    });
  }, []);

  const refreshCameraCount = useCallback(async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      setMultipleCameras(devs.filter(d => d.kind === 'videoinput').length > 1);
    } catch (e) {}
  }, []);
  useEffect(() => {
    if (!groupCall?.call_id || groupCall.status === 'incoming') return undefined;
    refreshCameraCount();
    const h = () => refreshCameraCount();
    navigator.mediaDevices?.addEventListener?.('devicechange', h);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', h);
  }, [groupCall?.call_id, groupCall?.status, refreshCameraCount]);

  const refreshParticipants = useCallback(() => {
    const room = roomRef.current;
    if (!room) { setParticipants([]); return; }
    const mk = (p, isLocal) => {
      let cameraPub = null;
      let screenPub = null;
      try {
        const pubs = Array.from(p.videoTrackPublications.values());
        cameraPub = pubs.find(pub => pub.source === Track.Source.Camera && !pub.isMuted && pub.track) || null;
        screenPub = pubs.find(pub => pub.source === Track.Source.ScreenShare && !pub.isMuted && pub.track) || null;
      } catch (e) {}
      return {
        identity: p.identity,
        name: p.name || p.identity,
        isLocal,
        isSpeaking: !!p.isSpeaking,
        micOn: !!p.isMicrophoneEnabled,
        cameraTrack: cameraPub?.track || null,
        screenTrack: screenPub?.track || null,
        quality: mapQuality(p.connectionQuality),
        away: !isLocal && awayRef.current.has(p.identity),
      };
    };
    try {
      const sp = room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
      setScreenSharing(!!(sp && !sp.isMuted && sp.track));
    } catch (e) {}
    try {
      setParticipants([
        mk(room.localParticipant, true),
        ...Array.from(room.remoteParticipants.values()).map(p => mk(p, false)),
      ]);
    } catch (e) {}
  }, []);

  const cleanup = useCallback(async (leaveServer = true) => {
    stopRing();
    if (navigator.vibrate) { try { navigator.vibrate(0); } catch (e) {} }
    ringbackRef.current?.stop();
    ringbackRef.current = null;
    closeBoost();
    awayRef.current = new Set();
    if (soloTimerRef.current) { clearInterval(soloTimerRef.current); soloTimerRef.current = null; }
    setSoloCountdown(null);
    suppressSoloRef.current = false;
    hadRemoteRef.current = false;
    setDominantSpeaker(null);
    speakerChangeAtRef.current = 0;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const room = roomRef.current;
    roomRef.current = null;
    const gc = groupCallRef.current;
    setGroupCall(null);
    setParticipants([]);
    setParticipantStates({});
    setCameraOn(false);
    setMicMuted(false);
    setScreenSharing(false);
    setCameraFacing('user');
    setConnectionQuality(null);
    setEncrypted(false);
    setCallSeconds(0);
    setCallChatToast(null);
    setCallChatUnread(0);
    if (chatToastTimerRef.current) { clearTimeout(chatToastTimerRef.current); chatToastTimerRef.current = null; }
    setActiveDevices({});
    if (audioHostRef.current) audioHostRef.current.innerHTML = '';
    try { if (room) await room.disconnect(); } catch (e) {}
    if (leaveServer && gc?.call_id && gc.status !== 'incoming') {
      try { await api.post(`/group-calls/${gc.call_id}/leave`); } catch (e) {}
    }
  }, [api, stopRing]);

  const connectRoom = useCallback(async (info, meta, opts = {}) => {
    const room = new Room({
      // pixelDensity 'screen' — subscribe to layers sized for the device's real
      // (retina) pixels, not CSS pixels; fixes blurry group video on phones
      adaptiveStream: { pixelDensity: 'screen' },
      dynacast: true,
      videoCaptureDefaults: { resolution: VideoPresets.h720.resolution, facingMode: 'user' },
      publishDefaults: {
        simulcast: true,
        videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
        videoEncoding: VideoPresets.h720.encoding,
      },
    });
    roomRef.current = room;
    const refresh = () => refreshParticipants();
    room
      .on(RoomEvent.ParticipantConnected, () => {
        ringbackRef.current?.stop();
        ringbackRef.current = null;
        refresh();
      })
      .on(RoomEvent.ParticipantDisconnected, (p) => {
        try { awayRef.current.delete(p.identity); } catch (e) {}
        refresh();
      })
      .on(RoomEvent.DataReceived, (payload, participant) => {
        if (!participant) return;
        try {
          const msg = JSON.parse(new TextDecoder().decode(payload));
          if (msg?.t === 'away') awayRef.current.add(participant.identity);
          else if (msg?.t === 'back') awayRef.current.delete(participant.identity);
          else return;
          refresh();
        } catch (e) {}
      })
      .on(RoomEvent.ConnectionQualityChanged, (q, participant) => {
        if (participant?.isLocal) setConnectionQuality(mapQuality(q));
        refresh();
      })
      .on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === 'audio') {
          try {
            const el = track.attach();
            audioHostRef.current?.appendChild(el);
            boostAttachTrack(track, el);
          } catch (e) {}
        }
        refresh();
      })
      .on(RoomEvent.TrackUnsubscribed, (track) => {
        boostDetachTrack(track);
        try { track.detach().forEach(el => el.remove()); } catch (e) {}
        refresh();
      })
      .on(RoomEvent.TrackMuted, refresh)
      .on(RoomEvent.TrackUnmuted, refresh)
      .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        if (speakers && speakers.length) {
          const id = speakers[0].identity;
          const now = Date.now();
          setDominantSpeaker(prev => {
            if (prev === id) return prev;
            if (now - speakerChangeAtRef.current < 1500) return prev; // anti-jitter hold
            speakerChangeAtRef.current = now;
            return id;
          });
        }
        refresh();
      })
      .on(RoomEvent.LocalTrackPublished, refresh)
      .on(RoomEvent.LocalTrackUnpublished, refresh)
      .on(RoomEvent.Disconnected, () => {
        if (groupCallRef.current && groupCallRef.current.status === 'active') cleanup(true);
      });
    await room.connect(info.url, info.token);
    if (navigator.vibrate) { try { navigator.vibrate(0); } catch (e) {} } // Android: kill any lingering ring vibration
    setEncrypted(true); // DTLS-SRTP transport encryption
    if (opts.micOn !== false) {
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
        setMicMuted(false);
      } catch (e) {
        toast.error('Could not access the microphone');
        setMicMuted(true);
      }
    } else {
      setMicMuted(true);
    }
    if (meta.media === 'video' && opts.camOn !== false) {
      try { await room.localParticipant.setCameraEnabled(true); setCameraOn(true); } catch (e) { setCameraOn(false); }
    }
    setGroupCall({ ...meta, status: 'active' });
    setCallSeconds(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCallSeconds(s => s + 1), 1000);
    // Item 8: soft ringback for the initiator while waiting alone for others to join
    if (meta.isInitiator && room.remoteParticipants.size === 0 && !ringbackRef.current) {
      ringbackRef.current = createRinger({ freq: 425, beep: 0.9, every: 3200 });
      ringbackRef.current.start();
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshParticipants, cleanup]);

  const _seedStates = useCallback((participantsMap) => {
    if (!participantsMap) return;
    setParticipantStates(Object.fromEntries(
      Object.entries(participantsMap).map(([k, v]) => [k, { state: v?.state, name: v?.name }])
    ));
  }, []);

  const _joinApi = useCallback(async (callId, baseMeta = {}, opts = {}) => {
    stopRing();
    try {
      const { data } = await api.post(`/group-calls/${callId}/join`);
      const meta = {
        call_id: callId,
        conversation_id: data.conversation_id,
        conversation_name: baseMeta.conversation_name || groupCallRef.current?.conversation_name || 'Group call',
        media: data.media || 'voice',
        status: 'connecting',
      };
      setGroupCall(meta);
      _seedStates(data.participants);
      await connectRoom(data, meta, opts);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join the call');
      cleanup(false);
    }
  }, [api, connectRoom, cleanup, stopRing, _seedStates]);

  const _startApi = useCallback(async (conversation, media, opts = {}) => {
    try {
      const { data } = await api.post('/group-calls', { conversation_id: conversation.id, media });
      const meta = {
        call_id: data.call_id,
        conversation_id: conversation.id,
        conversation_name: conversation.name || 'Group call',
        media: data.media || media,
        status: 'connecting',
        isInitiator: true,
      };
      setGroupCall(meta);
      setActiveConvCalls(m => ({ ...m, [conversation.id]: { call_id: data.call_id, media: meta.media, name: conversation.name } }));
      _seedStates(data.participants);
      await connectRoom(data, meta, opts);
    } catch (err) {
      if (err.response?.status === 409) {
        const activeId = err.response.headers?.['x-active-call-id'];
        if (activeId) return _joinApi(activeId, { conversation_name: conversation.name }, opts);
      }
      toast.error(err.response?.data?.detail || 'Could not start the group call');
      cleanup(false);
    }
  }, [api, connectRoom, cleanup, _joinApi, _seedStates]);

  // U2: every entry point lands on the pre-join (green room) screen first
  const joinGroupCall = useCallback((callId, conversation = null) => {
    if (oneToOneRef.current) { toast.info('You are already on a call'); return; }
    stopRing();
    const known = conversation?.id ? activeConvCallsRef.current?.[conversation.id] : null;
    setGroupCall({
      call_id: callId,
      conversation_id: conversation?.id || groupCallRef.current?.conversation_id,
      conversation_name: conversation?.name || groupCallRef.current?.conversation_name || 'Group call',
      media: known?.media || groupCallRef.current?.media || 'voice',
      status: 'prejoin',
      mode: 'join',
    });
  }, [stopRing]);

  const startGroupCall = useCallback((conversation, media = 'voice') => {
    if (groupCallRef.current || oneToOneRef.current) { toast.info('You are already on a call'); return; }
    setGroupCall({
      conversation_id: conversation.id,
      conversation_name: conversation.name || 'Group call',
      media,
      status: 'prejoin',
      mode: 'start',
      _conversation: conversation,
    });
  }, []);

  const confirmJoin = useCallback(async (opts = {}) => {
    const gc = groupCallRef.current;
    if (!gc || gc.status !== 'prejoin') return;
    if (gc.mode === 'start') await _startApi(gc._conversation, gc.media, opts);
    else await _joinApi(gc.call_id, gc, opts);
  }, [_startApi, _joinApi]);

  const cancelPrejoin = useCallback(() => {
    if (groupCallRef.current?.status === 'prejoin') setGroupCall(null);
  }, []);

  const acceptIncoming = useCallback(() => {
    const gc = groupCallRef.current;
    if (!gc || gc.status !== 'incoming') return;
    stopRing();
    setGroupCall({ ...gc, status: 'prejoin', mode: 'join' });
  }, [stopRing]);

  const declineIncoming = useCallback(async () => {
    const gc = groupCallRef.current;
    if (!gc || gc.status !== 'incoming') return;
    stopRing();
    setGroupCall(null);
    try { await api.post(`/group-calls/${gc.call_id}/decline`); } catch (e) {}
  }, [api, stopRing]);

  const leaveGroupCall = useCallback(() => cleanup(true), [cleanup]);

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !micMuted;
    try {
      await room.localParticipant.setMicrophoneEnabled(!next);
      setMicMuted(next);
      refreshParticipants();
    } catch (e) {}
  }, [micMuted, refreshParticipants]);

  const toggleCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !cameraOn;
    try {
      await room.localParticipant.setCameraEnabled(next);
      setCameraOn(next);
      refreshParticipants();
    } catch (e) {
      if (next) toast.error('Could not access the camera');
    }
  }, [cameraOn, refreshParticipants]);

  const canScreenShare = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !screenSharing;
    try {
      await room.localParticipant.setScreenShareEnabled(next);
      setScreenSharing(next);
      refreshParticipants();
    } catch (e) { /* picker cancelled */ }
  }, [screenSharing, refreshParticipants]);

  const flipCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room || !cameraOn) return;
    const next = cameraFacing === 'user' ? 'environment' : 'user';
    try {
      const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      if (pub?.track) {
        await pub.track.restartTrack({ facingMode: next });
        // Only un-mirror if the camera really switched to the back one
        const actual = pub.track.mediaStreamTrack?.getSettings?.().facingMode;
        setCameraFacing(actual === 'environment' ? 'environment' : 'user');
        refreshParticipants();
      }
    } catch (e) { toast.error('Could not switch camera'); }
  }, [cameraOn, cameraFacing, refreshParticipants]);


  // Solo auto-end: once others have been on the call and everyone else leaves,
  // give the last person 60s (with a Stay option) before ending automatically.
  const clearSoloTimer = useCallback(() => {
    if (soloTimerRef.current) { clearInterval(soloTimerRef.current); soloTimerRef.current = null; }
    setSoloCountdown(null);
  }, []);

  const stayOnCall = useCallback(() => {
    suppressSoloRef.current = true;
    clearSoloTimer();
  }, [clearSoloTimer]);

  useEffect(() => {
    if (groupCall?.status !== 'active') return;
    const remoteCount = participants.filter(p => !p.isLocal).length;
    if (remoteCount > 0) {
      hadRemoteRef.current = true;
      suppressSoloRef.current = false;
      clearSoloTimer();
      return;
    }
    if (hadRemoteRef.current && !suppressSoloRef.current && !soloTimerRef.current) {
      setSoloCountdown(60);
      soloTimerRef.current = setInterval(() => {
        setSoloCountdown(s => {
          if (s === null) return null;
          if (s <= 1) {
            clearInterval(soloTimerRef.current);
            soloTimerRef.current = null;
            cleanup(true);
            return null;
          }
          return s - 1;
        });
      }, 1000);
    }
  }, [participants, groupCall?.status, cleanup, clearSoloTimer]);

  // Tell the group when THIS side gets interrupted (backgrounded for a native
  // phone call / app switch) — same UX as the 1:1 hold/resume signal.
  useEffect(() => {
    if (groupCall?.status !== 'active') return undefined;
    const onVisibility = () => {
      const room = roomRef.current;
      if (!room) return;
      // While screen-sharing, switching windows/apps is EXPECTED — never signal away
      if (document.hidden && screenSharingRef.current) return;
      const t = document.hidden ? 'away' : 'back';
      try { room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify({ t })), { reliable: true }); } catch (e) {}
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [groupCall?.status]);

  const refreshActiveCall = useCallback(async (conversationId) => {
    try {
      const { data } = await api.get('/group-calls/active', { params: { conversation_id: conversationId } });
      setActiveConvCalls(m => {
        const n = { ...m };
        if (data.call) n[conversationId] = { call_id: data.call.id, media: data.call.media };
        else delete n[conversationId];
        return n;
      });
    } catch (e) {}
  }, [api]);

  // ── WS events ──
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const unsubscribe = onDeviceUpdate(async (_deviceId, data) => {
      if (!data?.type) return;
      if (Array.isArray(data.user_ids) && user?.id && !data.user_ids.includes(user.id)) return;
      if (data.type === 'chat_message') {
        // U10: surface chat messages for THIS call's conversation inside the call UI
        const gc = groupCallRef.current;
        const msg = data.message;
        if (gc && gc.status === 'active' && msg && data.conversation_id === gc.conversation_id
            && msg.sender_id !== user?.id && msg.type !== 'call' && !msg.deleted) {
          setCallChatUnread(n => n + 1);
          setCallChatToast({ id: msg.id, sender: msg.sender_name, text: msg.content || 'Sent an attachment' });
          if (chatToastTimerRef.current) clearTimeout(chatToastTimerRef.current);
          chatToastTimerRef.current = setTimeout(() => setCallChatToast(null), 4500);
        }
        return;
      }
      if (!data.type.startsWith('group_call')) return;

      if (data.type === 'group_call_invite') {
        if (data.initiator_id === user?.id) return;
        setActiveConvCalls(m => ({ ...m, [data.conversation_id]: { call_id: data.call_id, media: data.media || 'voice', name: data.conversation_name } }));
        if (groupCallRef.current || oneToOneRef.current) return; // busy — server marks missed on timeout
        setGroupCall({
          call_id: data.call_id,
          conversation_id: data.conversation_id,
          conversation_name: data.conversation_name || 'Group call',
          media: data.media || 'voice',
          status: 'incoming',
          initiator_name: data.initiator_name,
          joined_count: data.joined_count || 1,
          incoming_expires_at: Date.now() + 38000,
        });
        // Muted conversations show the invite silently (Mute Calls Too)
        if (!isConvMuted(data.conversation_id) && !ringRef.current) {
          ringRef.current = createRingtoneRinger(getStoredRingtoneChoice(), getCustomRingtoneUrl());
          ringRef.current.start();
        }
        if (!incomingTimeoutRef.current) {
          incomingTimeoutRef.current = setTimeout(() => {
            const cur = groupCallRef.current;
            if (cur && cur.call_id === data.call_id && cur.status === 'incoming') {
              stopRing();
              setGroupCall(null);
            }
          }, 38000);
        }
        return;
      }

      if (data.type === 'group_call_state') {
        if (data.participants) {
          setParticipantStates(Object.fromEntries(
            Object.entries(data.participants).map(([k, v]) => [k, { state: v?.state, name: v?.name }])
          ));
          const jc = Object.values(data.participants).filter(p => p?.state === 'joined').length;
          setGroupCall(g => (g && g.call_id === data.call_id && g.status === 'incoming' ? { ...g, joined_count: jc } : g));
        }
        setActiveConvCalls(m => ({ ...m, [data.conversation_id]: { ...(m[data.conversation_id] || {}), call_id: data.call_id, media: data.media || 'voice' } }));
        // My other device answered — silence this one
        const mine = data.participants?.[user?.id];
        const cur = groupCallRef.current;
        if (cur && cur.call_id === data.call_id && cur.status === 'incoming' && mine?.state && mine.state !== 'invited') {
          stopRing();
          setGroupCall(null);
        }
        return;
      }

      if (data.type === 'group_call_ended') {
        setActiveConvCalls(m => { const n = { ...m }; delete n[data.conversation_id]; return n; });
        const cur = groupCallRef.current;
        if (cur && cur.call_id === data.call_id) {
          if (cur.status === 'incoming') {
            stopRing();
            setGroupCall(null);
          } else {
            toast.info('Group call ended');
            cleanup(false);
          }
        }
      }
    });
    return unsubscribe;
  }, [isAuthenticated, user?.id, onDeviceUpdate, cleanup, stopRing]);

  // Reconcile with the server: pick up a live call after reload, focus, or a
  // missed WS invite (the server also re-broadcasts invites every 5s while ringing)
  const reconcileActiveCall = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get('/group-calls/active');
      const calls = data.calls || (data.call ? [data.call] : []);
      if (!calls.length) {
        // Server truth: no live call anywhere — clear stale banners (e.g. iOS
        // missed the group_call_ended WS event while the app was backgrounded)
        setActiveConvCalls(m => (Object.keys(m).length ? {} : m));
        return;
      }
      // B5: rebuild the whole map so banners for OTHER conversations survive
      setActiveConvCalls(Object.fromEntries(calls.map(c => (
        [c.conversation_id, { call_id: c.id, media: c.media, name: c.conversation_name }]
      ))));
      const call = calls.find(c => c.status === 'ringing' && c.participants?.[user.id]?.state === 'invited') || calls[0];
      const mine = call.participants?.[user.id];
      let ageSec = Infinity;
      try { ageSec = (Date.now() - new Date(call.created_at).getTime()) / 1000; } catch (e) {}
      if (
        call.status === 'ringing' && mine?.state === 'invited' && ageSec < 35 &&
        !groupCallRef.current && !oneToOneRef.current && call.initiator_id !== user.id
      ) {
        setGroupCall({
          call_id: call.id,
          conversation_id: call.conversation_id,
          conversation_name: call.conversation_name || 'Group call',
          media: call.media || 'voice',
          status: 'incoming',
          initiator_name: call.initiator_name,
          joined_count: Object.values(call.participants || {}).filter(p => p?.state === 'joined').length,
          incoming_expires_at: Date.now() + Math.max(3000, (38 - ageSec) * 1000),
        });
        if (!isConvMuted(call.conversation_id) && !ringRef.current) {
          ringRef.current = createRingtoneRinger(getStoredRingtoneChoice(), getCustomRingtoneUrl());
          ringRef.current.start();
        }
        if (!incomingTimeoutRef.current) {
          incomingTimeoutRef.current = setTimeout(() => {
            const cur = groupCallRef.current;
            if (cur && cur.call_id === call.id && cur.status === 'incoming') {
              stopRing();
              setGroupCall(null);
            }
          }, Math.max(3000, (38 - ageSec) * 1000));
        }
      }
    } catch (e) {}
  }, [api, user?.id, stopRing]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;
    reconcileActiveCall();
    const onFocus = () => reconcileActiveCall();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [isAuthenticated, user?.id, reconcileActiveCall]);

  // U8: re-ring one missed/declined/left member from the roster panel
  const ringAgain = useCallback(async (userId) => {
    const gc = groupCallRef.current;
    if (!gc?.call_id) return;
    try {
      await api.post(`/group-calls/${gc.call_id}/ring-again`, { user_id: userId });
      toast.success('Ringing again…');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not ring again');
    }
  }, [api]);

  // U4: device lists + hot-switching
  const listMediaDevices = useCallback(async () => {
    const by = { audioinput: [], videoinput: [], audiooutput: [] };
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      devs.forEach(d => {
        if (by[d.kind]) by[d.kind].push({ deviceId: d.deviceId, label: d.label || `${d.kind === 'audioinput' ? 'Microphone' : d.kind === 'videoinput' ? 'Camera' : 'Speaker'} ${by[d.kind].length + 1}` });
      });
    } catch (e) {}
    return by;
  }, []);

  const switchDevice = useCallback(async (kind, deviceId) => {
    const room = roomRef.current;
    if (!room || !deviceId) return;
    try {
      await room.switchActiveDevice(kind, deviceId);
      if (kind === 'audiooutput' && boostRef.current?.ctx?.setSinkId) {
        try { await boostRef.current.ctx.setSinkId(deviceId); } catch (e) {}
      }
      setActiveDevices(m => ({ ...m, [kind]: deviceId }));
      refreshParticipants();
    } catch (e) {
      toast.error('Could not switch device');
    }
  }, [refreshParticipants]);

  const canSetSpeaker = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

  const clearCallChatUnread = useCallback(() => {
    setCallChatUnread(0);
    setCallChatToast(null);
    if (chatToastTimerRef.current) { clearTimeout(chatToastTimerRef.current); chatToastTimerRef.current = null; }
  }, []);

  const value = {
    groupCall,
    participants,
    participantStates,
    micMuted,
    cameraOn,
    callSeconds,
    activeConvCalls,
    screenSharing,
    toggleScreenShare,
    canScreenShare,
    cameraFacing,
    flipCamera,
    canFlipCamera: multipleCameras,
    connectionQuality,
    encrypted,
    volumeBoost,
    toggleVolumeBoost,
    canBoostVolume: !IS_IOS,
    dominantSpeaker,
    soloCountdown,
    stayOnCall,
    startGroupCall,
    joinGroupCall,
    acceptIncoming,
    declineIncoming,
    leaveGroupCall,
    toggleMic,
    toggleCamera,
    refreshActiveCall,
    confirmJoin,
    cancelPrejoin,
    ringAgain,
    listMediaDevices,
    switchDevice,
    activeDevices,
    canSetSpeaker,
    callChatToast,
    callChatUnread,
    clearCallChatUnread,
  };

  return <GroupCallContext.Provider value={value}>{children}</GroupCallContext.Provider>;
};

export default GroupCallContext;
